import jQuery from 'jquery';

import { restoreCssParallaxAnimationEmulation } from '../../../animation/emulate-css-parallax-during-animation';
import { cancelPendingAnimationTimeouts } from '../../../animation/pending-animation-timeouts';
import { shouldClearLeftAfterAnimation } from '../../../animation/should-clear-left-after-animation';
import { isInBuilder } from '../../../utils-utils';
import { findElementsWithAnimations } from '../find-elements-with-animations';
import { getComputedDisplay } from '../get-computed-display';
import { pauseMedia } from '../pause-media';

const builderInteractionHiddenAttribute = 'data-divi-interactions-hidden';

/**
 * Check whether Visual Builder interaction hiding should use the attribute marker.
 *
 * Always prefer the marker in VB so remount/skipLoad hides cannot write stuck
 * inline `display: none !important` before force-visible classes paint. Frontend
 * keeps the inline hide path.
 *
 * @returns {boolean} Whether builder hidden state should use an attribute marker instead.
 */
const shouldUseBuilderHiddenMarker = (): boolean => isInBuilder();

/**
 * Reset animation state for an element so it can be animated again.
 * Restores et_animated class if the element was previously animated.
 *
 * @param {JQuery} $element Element to reset.
 */
const resetAnimationState = ($element: JQuery): void => {
  // A still-pending waypoint completion timeout would stamp `et_had_animation` mid-replay.
  cancelPendingAnimationTimeouts($element);

  // Check if element was animated (has et_had_animation means it completed animation)
  const wasAnimated = $element.hasClass('et_had_animation');

  // Remove the marker class that prevents re-animation
  $element.removeClass('et_had_animation');

  const clearLeft = shouldClearLeftAfterAnimation($element);

  // Remove animation classes if available
  if ('function' === typeof window.et_get_animation_classes) {
    const animationClasses = window.et_get_animation_classes();
    $element.removeClass(animationClasses.join(' '));
  }

  // Restore et_animated if the element was previously animated
  // (et_animated gets removed when animation completes, but we restore it when resetting)
  if (wasAnimated) {
    $element.addClass('et_animated');
  }

  // Remove animation CSS properties
  const animationResetCss: Record<string, string> = {
    'animation-delay': '',
    'animation-duration': '',
    'animation-timing-function': '',
    opacity: '',
    transform: '',
  };

  if (clearLeft) {
    animationResetCss.left = '';
  }

  $element.css(animationResetCss);

  // Restore native CSS parallax `fixed` when transform is cleared outside `removeAnimation`.
  restoreCssParallaxAnimationEmulation($element);
};

/**
 * Find all elements with animations (including nested ones) and reset their animation state.
 *
 * @param {JQuery} $container The container element to search within.
 */
const resetAnimationsForElements = ($container: JQuery): void => {
  // Reset container and all nested elements that have been animated
  $container
    .find('.et_had_animation')
    .addBack('.et_had_animation')
    .each((index, elem) => {
      resetAnimationState(jQuery(elem));
    });
};

/**
 * Reset animation state for a subtree so animations can be re-triggered when shown again.
 * Mirrors the post-hide animation reset in hideElement without changing inline display or pausing media.
 * Used when visibility is toggled via CSS classes (e.g. Dropdown) instead of hideElement().
 *
 * @param {HTMLElement} element Root of the subtree to reset.
 */
const resetAnimationsInSubtree = (element: HTMLElement): void => {
  const $container = jQuery(element);
  resetAnimationsForElements($container);
  findElementsWithAnimations($container);
};

/**
 * Hide an element by setting its display property to 'none'.
 * Stores the current display value for later restoration.
 * Resets animation state so animations can be re-triggered when the element becomes visible again.
 * Pauses any playing media (videos, iframe embeds) before hiding the element.
 *
 * @param {HTMLElement} element The HTML element to hide.
 */
const hideElement = (element: HTMLElement): void => {
  // Pause any playing media before hiding the element.
  pauseMedia(element);

  const currentDisplay = getComputedDisplay(element);
  if (currentDisplay !== 'none') {
    element.setAttribute('data-divi-original-display', currentDisplay);
  }

  if (shouldUseBuilderHiddenMarker()) {
    element.style.removeProperty('display');
    element.setAttribute(builderInteractionHiddenAttribute, 'true');
  } else {
    element.removeAttribute(builderInteractionHiddenAttribute);
    element.style.setProperty('display', 'none', 'important');
  }

  resetAnimationsInSubtree(element);
};

export { hideElement, resetAnimationState, resetAnimationsInSubtree };
